import * as HelpersModule from './helpers.js';
import { showChangesPreview } from './changesPreview.js';
import { themeManager, THEME_PROFILES } from './theme.js';

// Toast implementation
function ensureToaster(): HTMLElement {
  let toaster = document.querySelector('punica-toaster') as HTMLElement | null;
  if (!toaster) {
    // No factory: the toaster is a singleton host reached through
    // `View.showToast`, not something a caller composes. See showDialog below.
    // eslint-disable-next-line no-restricted-syntax
    toaster = document.createElement('punica-toaster');
    document.body.appendChild(toaster);
  }
  return toaster;
}

function showToast(options: {
  title?: string;
  message?: string;
  variant?: 'neutral' | 'info' | 'success' | 'warning' | 'error';
  placement?:
    | 'top-left'
    | 'top-center'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-center'
    | 'bottom-right';
  duration?: number;
  dismissible?: boolean;
}): HTMLElement {
  const {
    title = '',
    message = '',
    variant = 'neutral',
    duration = 1000,
    dismissible = true,
    placement = 'top-right'
  } = options;

  const toaster = ensureToaster();
  const shadow = (toaster as any).shadowRoot as ShadowRoot | null;

  if (!shadow) {
    // Fallback: use window.Toast if available
    if ((window as any).Toast?.show) {
      return (window as any).Toast.show(options);
    }
    throw new Error('punica-toaster shadow root not available');
  }

  const region =
    shadow.querySelector(`.region.${placement}`) ||
    shadow.querySelector('.region.top-right');

  if (!region) {
    throw new Error(`Toast region not found: ${placement}`);
  }

  // eslint-disable-next-line no-restricted-syntax
  const toast = document.createElement('punica-toast');
  toast.setAttribute('variant', variant);

  if (title) toast.setAttribute('title', title);
  if (message) toast.setAttribute('message', message);
  if (duration > 0) toast.setAttribute('duration', String(duration));

  toast.setAttribute('dismissible', String(dismissible));
  toast.setAttribute(
    'enter-from',
    placement.startsWith('top') ? 'top' : 'bottom'
  );

  region.prepend(toast);

  return toast;
}

/**
 * Escape caller text destined for `innerHTML`.
 *
 * `showDialog` builds its markup as a template string, so every interpolated
 * value is parsed as HTML. `html` and `footerHtml` are the documented opt-ins
 * for callers that mean markup; `title`, `message` and button labels are TEXT
 * and must not be able to introduce elements.
 *
 * This is not hypothetical. A caller passing server-provided text — a model
 * name, a filename, a remote error message — hands that text straight to the
 * parser. Measured 2026-08-07: MLflow accepts a registered model name of
 * `x<img src=q onerror=alert(1)>`, and `extension-experiments` puts the model
 * name in a promotion confirm, so opening that dialog executed it.
 */
function escapeHtmlText(text: string): string {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escaped message text, with newlines preserved as line breaks.
 *
 * HTML collapses whitespace, so a caller writing a two-paragraph confirm got one
 * run-on blob. Escaping first and only then inserting `<br>` keeps the caller's
 * text inert while honouring the shape they wrote.
 */
function messageToHtml(message: string): string {
  return escapeHtmlText(message).replace(/\r?\n/g, '<br>');
}

// Dialog implementation
async function showDialog(options: {
  title: string;
  message?: string;
  html?: string;
  buttons?: Array<{
    label: string;
    variant?: 'filled' | 'outlined' | 'text';
    color?: 'primary' | 'secondary' | 'error';
    action?: string;
  }>;
  footerHtml?: string;
  modal?: boolean;
  width?: string;
  height?: string;
  icon?: string;
  tone?: 'info' | 'warning' | 'error' | 'success';
  onMount?: (dialog: HTMLElement) => void;
}): Promise<string | undefined> {
  return new Promise((resolve) => {
    const {
      title,
      message,
      html,
      buttons = [{ label: 'OK', variant: 'filled', action: 'ok' }],
      footerHtml,
      modal = true,
      width,
      height,
      icon,
      tone,
      onMount
    } = options;

    const dialogId = `punica-dialog-${Date.now()}`;
    const existing = document.querySelector<HTMLElement>(`#${dialogId}`);
    existing?.remove();

    // No factory, deliberately: the body below is one template string wired
    // into `escapeHtmlText`/`messageToHtml`, and `dialogEscaping.test.ts` pins
    // exactly those strings. Moving to factories would make the escaping
    // structural (an improvement) but collides with the `html`/`footerHtml`
    // markup opt-ins — its own piece of work.
    // eslint-disable-next-line no-restricted-syntax
    const dialog = document.createElement('punica-dialog');
    dialog.id = dialogId;
    dialog.setAttribute('rounded', '');
    if (modal) dialog.setAttribute('modal', '');

    // `width` has to reach the HOST, not only the content slot.
    // `punica-dialog` writes `width: 440px` inline onto its shadow `<dialog>`
    // when it opens without a `width` attribute, and that dialog is
    // `overflow: hidden` — so a content slot styled `width: 90vw` was laid out
    // at 1516px inside a 472px box and simply cut off. Measured in the app on
    // 2026-08-28, and reproduced in isolation: same markup, host attribute
    // absent → inner 472 and clipped, present → inner 1548 and not.
    //
    // Only when the caller asked. With no `width` the attribute stays off and
    // the component keeps its 440px default, so every existing dialog renders
    // exactly as before.
    if (width) dialog.setAttribute('width', width);

    const style: string[] = [`width: ${width || '440px'}`];
    if (height) style.push(`height: ${height}`);
    const styleAttr = ` style="${style.join('; ')}"`;

    // A tone paints a colored leading icon chip in the header. With no
    // tone/icon the header is just the title (still the modern spacing below).
    const toneColor = tone ? `var(--${tone}-main)` : '';
    const defaultToneIcon: Record<string, string> = {
      info: 'fa-circle-info',
      warning: 'fa-triangle-exclamation',
      error: 'fa-circle-exclamation',
      success: 'fa-circle-check'
    };
    const iconClass = icon || (tone ? defaultToneIcon[tone] : '');
    const headerIcon =
      iconClass && toneColor
        ? `<span style="flex:0 0 auto; width:34px; height:34px; display:inline-flex; align-items:center; justify-content:center; border-radius:50%; background: color-mix(in srgb, ${toneColor} 16%, transparent); color:${toneColor}; font-size:16px;"><i class="fa-duotone fa-solid ${iconClass}"></i></span>`
        : '';

    const contentHtml =
      html ||
      (message
        ? `<punica-typography style="color: var(--text-secondary); line-height: 1.6;">${messageToHtml(message)}</punica-typography>`
        : '');

    dialog.innerHTML = `
      <punica-dialog-header>
        <punica-box style="display:flex; align-items:center; gap:12px;">
          ${headerIcon}
          <punica-typography fontweight="bold" style="color: var(--text-primary); font-size: 16px;">${escapeHtmlText(title)}</punica-typography>
        </punica-box>
      </punica-dialog-header>
      <punica-dialog-content${styleAttr}>
        <punica-box style="display:flex; flex-direction: column; gap: 10px; padding: 4px 2px 8px;">
          ${contentHtml}
        </punica-box>
      </punica-dialog-content>
      <punica-dialog-footer>
        ${
          footerHtml ||
          `
          <punica-box style="display:flex; width:100%; box-sizing:border-box; gap: 8px; justify-content: flex-end; padding-top: 14px; border-top: 1px solid var(--divider);">
            ${buttons
              .map((btn, idx) => {
                const btnId = `${dialogId}-btn-${idx}`;
                const variant = btn.variant || 'outlined';
                const color = btn.color || 'primary';
                return `<punica-button id="${btnId}" variant="${variant}" color="${color}" size="small" rounded>${escapeHtmlText(btn.label)}</punica-button>`;
              })
              .join('')}
          </punica-box>
        `
        }
      </punica-dialog-footer>
    `;

    document.body.appendChild(dialog);

    queueMicrotask(() => {
      try {
        dialog.setAttribute('open', 'true');
        (dialog as any).open = true;
        onMount?.(dialog);
      } catch {
        // ignore
      }
    });

    const closeDialog = (action?: string) => {
      try {
        dialog.remove();
      } catch {
        // ignore
      }
      resolve(action);
    };

    buttons.forEach((btn, idx) => {
      const btnId = `${dialogId}-btn-${idx}`;
      const button = dialog.querySelector(`#${btnId}`);
      button?.addEventListener('click', () => {
        closeDialog(btn.action || btn.label.toLowerCase());
      });
    });

    (dialog as any).addEventListener?.('close', () => {
      closeDialog(undefined);
    });
  });
}

// Confirm implementation
async function showConfirm(options: {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'info' | 'warning' | 'error';
}): Promise<boolean> {
  const {
    title = 'Confirm',
    message,
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
    variant = 'info'
  } = options;

  const result = await showDialog({
    title,
    message,
    tone: variant,
    buttons: [
      {
        label: cancelLabel,
        variant: 'text',
        action: 'cancel'
      },
      {
        label: confirmLabel,
        variant: 'filled',
        color: variant === 'error' ? 'error' : 'primary',
        action: 'confirm'
      }
    ]
  });

  return result === 'confirm';
}

// Alert implementation
async function showAlert(options: {
  title?: string;
  message: string;
  variant?: 'info' | 'warning' | 'error' | 'success';
  buttonLabel?: string;
}): Promise<void> {
  const { title, message, variant = 'info', buttonLabel = 'OK' } = options;

  const variantTitle =
    title ||
    (variant === 'success'
      ? 'Success'
      : variant === 'warning'
        ? 'Warning'
        : variant === 'error'
          ? 'Error'
          : 'Information');

  await showDialog({
    title: variantTitle,
    message,
    tone: variant,
    buttons: [
      {
        label: buttonLabel,
        variant: 'filled',
        color: variant === 'error' ? 'error' : 'primary',
        action: 'ok'
      }
    ]
  });
}

// Prompt implementation
async function showPrompt(options: {
  title?: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: 'text' | 'password' | 'number';
}): Promise<string | undefined> {
  return new Promise((resolve) => {
    const {
      title = 'Prompt',
      message,
      placeholder = '',
      defaultValue = '',
      confirmLabel = 'OK',
      cancelLabel = 'Cancel',
      type = 'text'
    } = options;

    const dialogId = `punica-prompt-${Date.now()}`;
    const inputId = `${dialogId}-input`;

    // The markup below writes the key adornment by hand, so it needs the
    // stylesheet the input factory would otherwise have brought with it.
    HelpersModule.ensureSecretAdornmentStyles();

    // showDialog removes the dialog element BEFORE its promise resolves, so
    // reading the input from the DOM in `.then` finds nothing (detached) and
    // yields an empty value — the prompt silently returns undefined even on
    // confirm. Capture the value live via `onMount` instead: mirror the input
    // into a closure on every keystroke while the dialog is still mounted, and
    // read that closure at resolve time.
    let currentValue = defaultValue;

    showDialog({
      title,
      // `html` is the markup opt-in, so `showDialog` does not escape it — this
      // block builds the markup and is therefore responsible for its own
      // interpolations. `placeholder` and `defaultValue` sit inside attributes,
      // where an unescaped quote would end the attribute early.
      html: `
        <punica-box style="display:flex; flex-direction: column; gap: 10px;">
          <punica-typography>${messageToHtml(message)}</punica-typography>
          <punica-input
            id="${inputId}"
            type="${escapeHtmlText(type)}"
            placeholder="${escapeHtmlText(placeholder)}"
            value="${escapeHtmlText(defaultValue)}"
            rounded>
            ${
              type === 'password'
                ? '<i class="fa-solid fa-key punica-secret-adornment" slot="endAdornment" aria-hidden="true"></i>'
                : ''
            }
          </punica-input>
        </punica-box>
      `,
      buttons: [
        {
          label: cancelLabel,
          variant: 'outlined',
          action: 'cancel'
        },
        {
          label: confirmLabel,
          variant: 'filled',
          action: 'confirm'
        }
      ],
      onMount: (dialog) => {
        const input = dialog.querySelector(`#${inputId}`) as any;
        if (!input) return;
        const sync = () => {
          const v = input.value ?? input.getAttribute?.('value');
          if (typeof v === 'string') currentValue = v;
        };
        input.addEventListener('input', sync);
        input.addEventListener('change', sync);
        // Confirm on Enter for keyboard-only save.
        input.addEventListener('keydown', (ev: KeyboardEvent) => {
          if (ev.key === 'Enter') {
            sync();
            const btn = dialog.querySelector(
              `#${dialogId}-btn-1`
            ) as HTMLElement | null;
            btn?.click();
          }
        });
        setTimeout(() => input.focus?.(), 0);
      }
    }).then((action) => {
      if (action === 'confirm') {
        const value = (currentValue ?? '').trim();
        resolve(value ? currentValue : undefined);
      } else {
        resolve(undefined);
      }
    });
  });
}

// Information Messages implementation
async function showInformationMessage<T extends string>(
  message: string,
  ...items: T[]
): Promise<T | undefined> {
  if (items.length === 0) {
    await showAlert({
      message,
      variant: 'info',
      title: 'Information'
    });
    return undefined;
  }

  const result = await showDialog({
    title: 'Information',
    message,
    buttons: items.map((item) => ({
      label: item,
      variant: 'outlined' as const,
      action: item
    }))
  });

  return result as T | undefined;
}

async function showWarningMessage<T extends string>(
  message: string,
  ...items: T[]
): Promise<T | undefined> {
  if (items.length === 0) {
    await showAlert({
      message,
      variant: 'warning',
      title: 'Warning'
    });
    return undefined;
  }

  const result = await showDialog({
    title: 'Warning',
    message,
    buttons: items.map((item) => ({
      label: item,
      variant: 'outlined' as const,
      action: item
    }))
  });

  return result as T | undefined;
}

async function showErrorMessage<T extends string>(
  message: string,
  ...items: T[]
): Promise<T | undefined> {
  if (items.length === 0) {
    await showAlert({
      message,
      variant: 'error',
      title: 'Error'
    });
    return undefined;
  }

  const result = await showDialog({
    title: 'Error',
    message,
    buttons: items.map((item) => ({
      label: item,
      variant: 'outlined' as const,
      action: item
    }))
  });

  return result as T | undefined;
}

const view = {
  showInformationMessage,
  showWarningMessage,
  showErrorMessage,
  showDialog,
  showConfirm,
  showAlert,
  showPrompt,
  showToast,
  showChangesPreview,
  Helpers: HelpersModule.Helpers,
  Theme: {
    manager: themeManager,
    profiles: THEME_PROFILES
  }
};

export default view;
